import { useSearchParams } from "react-router";

import { PartialUseTableDataOptions, getKeyWithAddon, useTableData } from "@nens/react/hooks";

/**
 * Wrapper around useTableData that automatically wires up search params from the URL,
 * so pagination state is reflected in (and restored from) the URL.
 *
 * Pass any other useTableData options (initialSearchField, initialPage, etc.) as normal.
 * The searchParams / setSearchParams / hasSearchParams fields are handled for you.
 */

export const getSearchStringParam = ({
  searchField,
  searchString,
  keyAddon,
}: {
  keyAddon: UseRouterKeyAddon;
  searchField: string;
  searchString: string;
}) =>
  `${getKeyWithAddon(keyAddon, "searchField")}=${searchField}&${getKeyWithAddon(
    keyAddon,
    "searchString"
  )}=${searchString}`;

export const KEY_ADDON_MAP = {
  GMN: "GMN",
  BRO: "BRO",
  UPLOAD_TASK: "UploadTask",
  GLD: "GLD",
  GAR: "GAR",
  GPD: "GPD",
  GUF: "GUF",
  FRD: "FRD",
  GMW: "GMW",
  SELECT_GMN: "SelectGmn",
  GMN_FORM: "GmnForm",
} as const;

export type UseRouterKeyAddon = (typeof KEY_ADDON_MAP)[keyof typeof KEY_ADDON_MAP];

type UseRouterTableDataOptions =
  | ({ keyAddon: UseRouterKeyAddon; hasSearchParams?: true } & Omit<
      PartialUseTableDataOptions,
      "searchParams" | "setSearchParams" | "keyAddon" | "hasSearchParams"
    >)
  | ({ hasSearchParams: false } & Omit<PartialUseTableDataOptions, "hasSearchParams" | "keyAddon">);

export function useRouterTableData(options: UseRouterTableDataOptions) {
  const [searchParams, setSearchParams] = useSearchParams();

  return useTableData(
    "keyAddon" in options
      ? { ...options, searchParams, setSearchParams, hasSearchParams: true }
      : options
  );
}
